import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { historyService } from "./history";

export type StoreState = {
  code: string | undefined;
  codeSystem: string | undefined;
  scrollRef: MutableRefObject<Record<string, HTMLDivElement | null>>;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  hierarchyScrollRef: MutableRefObject<Record<string, HTMLDivElement | null>>;
  showInactiveOnLoad: boolean;
  excludedSearchFields: string[];
};

export type StoreActions = {
  updateCodeValue: (value?: string) => void;
  updateCodeSystem: (system: string) => void;
  updateScrollRef: (key: string, value: HTMLDivElement | null) => void;
  updateInputRef?: (ref: HTMLInputElement | null) => void;
  updateHierarchyScrollRef: (key: string, value: HTMLDivElement | null) => void;
  updateShowInactiveOnLoad: (show: boolean) => void;
  addExcludedSearchField: (field: string) => void;
  removeExcludedSearchField: (field: string) => void;
  updateExcludedSearchFields: (fields: string[]) => void;
};

// Store codeSystem in a ref so updateCodeValue can access it without being recreated
export const useStore = (): [StoreState, StoreActions] => {
  const [code, setCode] = useState<string | undefined>("");
  const [codeSystem, setCodeSystem] = useState<string | undefined>("");
  const codeSystemRef = useRef<string | undefined>(codeSystem);

  // Keep ref in sync with state (must be in useEffect, not during render)
  useEffect(() => {
    codeSystemRef.current = codeSystem;
  }, [codeSystem]);

  const scrollRef = useRef<Record<string, HTMLDivElement | null>>({});
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hierarchyScrollRef = useRef<Record<string, HTMLDivElement | null>>({});
  const [showInactiveOnLoad, setShowInactiveOnLoad] = useState<boolean>(false);
  const [excludedSearchFields, setExcludedSearchFields] = useState<string[]>(
    []
  );

  const updateCodeValue = useCallback((value?: string) => {
    if (value && codeSystemRef.current) {
      historyService.addToHistory(codeSystemRef.current, value);
    }
    setCode(value);
  }, []);

  const updateCodeSystem = useCallback((system: string) => {
    setCodeSystem(system);
  }, []);

  const updateScrollRef = useCallback(
    (key: string, ref: HTMLDivElement | null) => {
      scrollRef.current[key] = ref;
    },
    []
  );

  const updateInputRef = useCallback((ref: HTMLInputElement | null) => {
    inputRef.current = ref;
  }, []);

  const updateHierarchyScrollRef = useCallback(
    (key: string, ref: HTMLDivElement | null) => {
      hierarchyScrollRef.current[key] = ref;
    },
    []
  );

  const updateShowInactiveOnLoad = useCallback((show: boolean) => {
    setShowInactiveOnLoad(show);
  }, []);

  const addExcludedSearchField = useCallback((field: string) => {
    setExcludedSearchFields((prev) => [...prev, field]);
  }, []);

  const removeExcludedSearchField = useCallback((field: string) => {
    setExcludedSearchFields((prev) =>
      prev.filter((excludedField) => excludedField !== field)
    );
  }, []);

  const updateExcludedSearchFields = useCallback((fields: string[]) => {
    setExcludedSearchFields(fields);
  }, []);

  const state: StoreState = {
    code,
    codeSystem,
    scrollRef,
    inputRef,
    hierarchyScrollRef,
    showInactiveOnLoad,
    excludedSearchFields
  };

  const actions: StoreActions = {
    updateCodeValue,
    updateCodeSystem,
    updateScrollRef,
    updateInputRef,
    updateHierarchyScrollRef,
    updateShowInactiveOnLoad,
    addExcludedSearchField,
    removeExcludedSearchField,
    updateExcludedSearchFields
  };

  return [state, actions];
};
